import Agent from '@portal/env/agent';
import { emptyAuthTokens } from '@portal/env/stores/authTokens/authTokens';
import { isServer } from '@portal/env/ssr/ServerSideRenderManager';
import type { BruntwoodCacheTable } from '@common/utils/azure/tables/BruntwoodCacheTable';
import {
  NEXUDUS_BOOTSTRAP_CACHE_EXPIRY_MS,
  NexudusBootstrapEndpoint,
  nexudusBootstrapCacheKey,
} from '@common/utils/nexudus/nexudusBootstrapCache';

/**
 * Server-side read path for the two Nexudus calls that bootstrap every render:
 *
 *   GET  /en/business
 *   POST /en/mobileapp/Configuration
 *
 * They are served from the tenant's public site host (`/en/…`) rather than the
 * REST API, which is why they sit outside `NexudusService` — but the values
 * follow its persistent-cache pattern: they live in the shared Azure
 * `BruntwoodCacheTable`, kept fresh by companion-auth's CacheCop
 * `BootstrapPrimer` (10-minute cadence, 24-hour expiry). These are the two
 * endpoints behind the July 2026 incidents: 99.5% of the 21 Jul rate-limit
 * storm — see `docs/topics/nexudus-incident-report-2026-07-21.md`.
 *
 * Two deliberate differences from `NexudusService.cached()`:
 *
 * - **Cache trouble never fails a render.** Any cache-infrastructure error
 *   (unconfigured, unreachable, malformed) falls through to a direct Nexudus
 *   call — the cache must not become a new way for the portal to go down.
 * - **Direct fetches are anonymous** (`emptyAuthTokens`). Both responses are
 *   space-scoped, verified against the sandbox to be identical with and
 *   without a bearer token; fetching without one guarantees a per-user
 *   response can never enter the shared cache, by construction.
 *
 * On a miss the direct result is written back (fire-and-forget), so one
 * successful fetch stops every other render missing and a cold start
 * self-heals within seconds.
 *
 * Cached values are shape-checked on read: the table stores whatever the
 * primer fetched, so a structurally wrong entry (schema drift, manual edit)
 * would otherwise be served for up to 24 hours and crash the stores instead
 * of triggering the fall-through. An invalid entry is treated as a miss and
 * overwritten by the write-back.
 *
 * Server-only: callers must guard with `isServer()`. The Azure SDK and pino
 * are imported dynamically so this module stays safe to reach from client
 * bundles via the stores.
 */

type Logger = { warn: (obj: unknown, msg: string) => void };

let log: Logger | undefined;
const warn = (error: unknown, message: string): void => {
  if (log) log.warn({ err: error }, message);
  else console.warn(message, error);
};

let tablePromise: Promise<BruntwoodCacheTable | undefined> | null = null;

const getCacheTable = (): Promise<BruntwoodCacheTable | undefined> => {
  if (!isServer()) return Promise.resolve(undefined);

  if (!tablePromise) {
    // Deliberately not `defaultServices()` — that would couple every SSR
    // render to Mongo + Firebase initialisation. `createCacheIfConfigured`
    // resolves undefined when no storage endpoint is configured (e.g. local
    // dev), in which case every load is a direct call.
    tablePromise = Promise.all([
      import('@common/utils/azure/tables/BruntwoodStorageTables'),
      import('@portal/env/utils/serversideLogger'),
    ])
      .then(([{ BruntwoodStorageTables }, { logger }]) => {
        log = logger.child({ module: 'nexudusBootstrapCache' });
        // The table gets the portal's logger too: on its default console logger
        // a refused write goes to raw stdout and never reaches App Insights,
        // which is how an over-limit /en/business write stayed invisible
        // through the 103.36.0 release.
        return BruntwoodStorageTables.createDefault(process.env, {
          logger,
        }).createCacheIfConfigured();
      })
      .catch((error: unknown) => {
        warn(
          error,
          'Nexudus bootstrap cache unavailable, falling back to direct calls',
        );
        // Reset so a transient failure (rather than absent config) is retried
        // on the next render instead of pinning direct calls until restart.
        tablePromise = null;
        return undefined;
      });
  }
  return tablePromise;
};

const anonymousAgent = (host: string) => new Agent(host, emptyAuthTokens);

/** One `{ Name, Value }` pair from `POST /en/mobileapp/Configuration`.
 *
 * `Value` is null for any setting the space has not configured — 171 of the 418
 * entries in production. The stores copy it through to `configuration[Name]`
 * untouched, so null is a normal value here, not a malformed entry. */
export interface NexudusConfigurationEntry {
  Name: string;
  Value: string | null;
}

/** `GET /en/business` returns the location-tree object — an `Id`-bearing
 * object, never an array. */
const isValidBusiness = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  'Id' in value;

/** `POST /en/mobileapp/Configuration` returns an array of `{ Name, Value }`
 * pairs; the stores `.reduce()` over it, so every entry must conform.
 *
 * `Value` must be present but may be null — checking only that `Name` is a
 * string would let `undefined` through, which `.reduce()` would silently store
 * as a missing setting. */
const isValidConfiguration = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every((entry) => {
    if (typeof entry !== 'object' || entry === null) return false;
    const { Name, Value } = entry as NexudusConfigurationEntry;
    return (
      typeof Name === 'string' && (typeof Value === 'string' || Value === null)
    );
  });

const load = async <T>(
  endpoint: NexudusBootstrapEndpoint,
  host: string,
  isValid: (value: unknown) => boolean,
  direct: () => Promise<T>,
): Promise<T> => {
  const key = nexudusBootstrapCacheKey(endpoint, host);
  let table: BruntwoodCacheTable | undefined;

  try {
    table = await getCacheTable();
    if (table) {
      const held = await table.getEntry<T>(key);
      if (held !== undefined) {
        if (isValid(held)) return held;
        // Treated as a miss: fall through, and let the write-back below
        // replace the bad entry with a known-good response.
        warn(
          held,
          `Cached ${key} failed shape validation, calling Nexudus directly`,
        );
      }
    }
  } catch (error) {
    warn(
      error,
      `Reading ${key} from the bootstrap cache failed, calling Nexudus directly`,
    );
  }

  const value = await direct();

  // Write-back: fire-and-forget — the render must not wait on, or fail with,
  // the cache write.
  if (table) {
    table
      .cacheEntry({
        key,
        value,
        expiryAfterMs: NEXUDUS_BOOTSTRAP_CACHE_EXPIRY_MS,
      })
      .catch((error: unknown) =>
        warn(error, `Writing ${key} to the bootstrap cache failed`),
      );
  }

  return value;
};

/** `GET /en/business` for `host`, via the shared bootstrap cache. */
export const loadCachedBusiness = (host: string): Promise<unknown> =>
  load('business', host, isValidBusiness, () =>
    anonymousAgent(host).Business.getBusiness(),
  );

/** `POST /en/mobileapp/Configuration` for `host`, via the shared bootstrap cache. */
export const loadCachedConfiguration = (
  host: string,
): Promise<NexudusConfigurationEntry[]> =>
  load('configuration', host, isValidConfiguration, () =>
    anonymousAgent(host).Business.getConfiguration(),
  );

/** Test seam — forgets the resolved cache table so mocks take effect per test. */
export const resetNexudusBootstrapCache = (): void => {
  tablePromise = null;
  log = undefined;
};
